/**
 * Search ministers by CI, ADB code or name (public RPC, minimal fields).
 * Many ministers don't know their code by heart — CI or name works too.
 * The CI needs only its number (no extension) and the code's dash is optional.
 */

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { lookupMinister } from '../publicRegistrationRepository';
import type { MinisterLookupResult } from '../types';

interface MinisterLookupProps {
  eventId: string;
  placeholder?: string;
  /** Called with the result and the raw query (lets caller reuse a CI typed as query) */
  onSelect: (minister: MinisterLookupResult, query: string) => void;
  /** Disable picking ministers already registered (self-registration) */
  blockRegistered?: boolean;
}

export function MinisterLookup({ eventId, placeholder, onSelect, blockRegistered }: MinisterLookupProps) {
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');

  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 350);
    return () => clearTimeout(t);
  }, [query]);

  const { data: results = [], isFetching } = useQuery({
    queryKey: ['public_lookup_minister', eventId, debounced],
    queryFn: () => lookupMinister(eventId, debounced),
    enabled: debounced.length >= 3,
    staleTime: 60 * 1000,
  });

  return (
    <div className="space-y-2">
      <div className="relative">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder ?? 'CI (ej. 1234567), código (ej. 14-2429) o nombre'}
          className="pl-9"
          autoComplete="off"
        />
        {isFetching && (
          <Loader2 className="absolute right-3 top-1/2 size-4 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
      </div>

      <p className="text-xs text-muted-foreground">
        Ejemplos: CI <span className="font-medium">1234567</span> (solo el número, sin extensión) · código de ministro{' '}
        <span className="font-medium">14-2429</span>
      </p>

      {debounced.length >= 3 && !isFetching && results.length === 0 && (
        <p className="text-sm text-muted-foreground">
          Sin resultados. Revise el dato ingresado: pruebe con su número de CI o con su nombre.
        </p>
      )}

      {results.length > 0 && (
        <ul className="divide-y rounded-md border">
          {results.map((m) => {
            const blocked = blockRegistered && m.already_registered;
            return (
              <li key={m.id}>
                <button
                  type="button"
                  disabled={blocked}
                  onClick={() => onSelect(m, debounced)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-muted disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <span className="min-w-0">
                    <span className="block truncate font-medium">{m.full_name}</span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {m.adb_code}
                      {m.district_name && ` · ${m.district_name}`}
                      {m.region_name && ` · ${m.region_name}`}
                    </span>
                  </span>
                  {m.already_registered && (
                    <span className="shrink-0 text-xs text-muted-foreground">Ya inscrito</span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
